import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import "@/App.css";

// Import des composants
import Dashboard from "./components/Dashboard";
import EmployeesManager from "./components/EmployeesManager";
import ClientsManager from "./components/ClientsManager";
import HousingsManager from "./components/HousingsManager";
import PlanningCalendar from "./components/PlanningCalendar";
import GroupedPlanning from "./components/GroupedPlanning";
import WeeklyTimeline from "./components/WeeklyTimeline";
import IntegrationsManager from "./components/IntegrationsManager";
import GoogleCalendarCallback from "./components/GoogleCalendarCallback";
import Login from "./components/Login";
import UsersManager from "./components/UsersManager";
import Settings from "./components/Settings";
import StockClients, { hasLowStock } from "./components/StockClients";
import MobileNav from "./components/MobileNav";
import ParuresSalesRecap from "./components/ParuresSalesRecap";
import FacturationRecap from "./components/FacturationRecap";
import BilanRecap from "./components/BilanRecap";
import EmployeeDay from "./components/EmployeeDay";
import CitationsManager from "./components/CitationsManager";
import ICalSync from "./components/ICalSync";
import PhotosManagerSimple from "./components/PhotosManagerSimple";
import SyncAdmin from "./components/SyncAdmin";
import ResetPassword from "./components/ResetPassword";
import GestionEquipes from "./components/GestionEquipes";
import FirstTimeSetup from "./components/FirstTimeSetup";
import SuperAdminDashboard from "./components/SuperAdminDashboard";

// Import du prefetching
import { prefetchOnHover, prefetchAll } from "./utils/prefetch";
import { installMoveGuard } from "./utils/moveGuard";

installMoveGuard();

// Import du contexte d'authentification
import { AuthProvider, useAuth } from "./context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hook pour vérifier si l'app a besoin d'être configurée (première utilisation)
const useSetupStatus = () => {
  const [setupStatus, setSetupStatus] = React.useState({ loading: true, needsSetup: false });
  
  const checkStatus = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API}/setup/status`);
      setSetupStatus({ 
        loading: false, 
        needsSetup: response.data.needs_setup 
      });
    } catch (error) {
      console.log('Setup status check failed, assuming configured');
      setSetupStatus({ loading: false, needsSetup: false });
    }
  }, []);
  
  React.useEffect(() => {
    checkStatus();
  }, [checkStatus]);
  
  return { ...setupStatus, recheckStatus: checkStatus };
};

// Hook pour récupérer la config de l'app
const useAppConfig = () => {
  const [appConfig, setAppConfig] = React.useState(null);
  
  React.useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/setup/config`);
        setAppConfig(response.data);
      } catch (error) {
        console.log('App config not available');
        setAppConfig({ 
          app_name: 'Planning Ménage Pro',
          logo_url: null
        });
      }
    };
    fetchConfig();
  }, []);
  
  return appConfig;
};

// Hook pour récupérer la config démo
const useDemoConfig = () => {
  const [demoConfig, setDemoConfig] = React.useState(null);
  
  React.useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/demo/config`);
        setDemoConfig(response.data);
      } catch (error) {
        console.log('Demo config not available');
        setDemoConfig({ enabled: false });
      }
    };
    fetchConfig();
  }, []);
  
  return demoConfig;
};

// Protection anti-iframe (empêche l'intégration sur d'autres sites)
const AntiCloneProtection = () => {
  React.useEffect(() => {
    // Empêcher l'affichage dans un iframe externe
    if (window.self !== window.top) {
      try {
        // Vérifier si le parent est autorisé
        const parentOrigin = document.referrer;
        const currentOrigin = window.location.origin;
        if (!parentOrigin.includes(currentOrigin) && !parentOrigin.includes('emergent')) {
          document.body.innerHTML = '<h1 style="text-align:center;margin-top:50px;">⛔ Accès non autorisé</h1>';
        }
      } catch (e) {
        document.body.innerHTML = '<h1 style="text-align:center;margin-top:50px;">⛔ Accès non autorisé</h1>';
      }
    }
    
    // Désactiver le clic droit en mode démo
    const handleContextMenu = (e) => {
      if (document.body.classList.contains('demo-mode')) {
        e.preventDefault();
        return false;
      }
    };
    
    // Désactiver certaines combinaisons de touches
    const handleKeyDown = (e) => {
      if (document.body.classList.contains('demo-mode')) {
        // Ctrl+S, Ctrl+U, F12
        if ((e.ctrlKey && (e.key === 's' || e.key === 'u')) || e.key === 'F12') {
          e.preventDefault();
          return false;
        }
      }
    };
    
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  
  return null;
};

// Bannière Démo dynamique
const DemoBanner = ({ config }) => {
  if (!config || !config.enabled) return null;
  
  // Ajouter classe au body pour les protections
  React.useEffect(() => {
    document.body.classList.add('demo-mode');
    return () => document.body.classList.remove('demo-mode');
  }, []);
  
  return (
    <div className="bg-gradient-to-r from-orange-500 to-pink-500 text-white text-center py-2 px-4 text-sm font-medium">
      🎯 {config.banner_message || `VERSION DÉMONSTRATION - ${config.site_name}`} - 
      <a href={`mailto:${config.contact_email}`} className="underline font-bold ml-1">
        {config.contact_phone ? `${config.contact_phone} - ` : ''}Contactez-nous
      </a>
    </div>
  );
};

// Navigation Component
const Navigation = ({ appConfig }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [lowStock, setLowStock] = useState(false);
  const [precommandesCount, setPrecommandesCount] = useState(0);

  const { user, logout, isAdmin, isSuperAdmin, canAccess } = useAuth();
  
  // Vérifier si on est en mode "viewing tenant" (super admin visualisant un tenant)
  const isViewingTenant = localStorage.getItem("superAdminMode") === "true";
  const viewingTenantName = localStorage.getItem("viewingTenantName");

  const appName = appConfig?.company_name || appConfig?.app_name || 'Gestion Planning';
  const appSubtitle = 'Gestion de Planning';
  const logoUrl = appConfig?.logo_url;

  // Fonction pour retourner au Super Admin
  const returnToSuperAdmin = () => {
    localStorage.removeItem("superAdminMode");
    localStorage.removeItem("viewingTenantId");
    localStorage.removeItem("viewingTenantName");
    navigate("/super-admin");
  };

  // Vérifier le stock et les précommandes au chargement et toutes les 5 minutes
  // Super admin : skip SEULEMENT si aucun tenant n'est visualisé (sinon on charge comme un admin)
  useEffect(() => {
    const viewingTenantId = typeof window !== "undefined" ? localStorage.getItem("viewingTenantId") : null;
    if (isSuperAdmin() && !viewingTenantId) return;

    const checkStock = async () => {
      const hasLow = await hasLowStock();
      setLowStock(hasLow);
    };
    
    const checkPrecommandes = async () => {
      try {
        const response = await axios.get(`${API}/precommandes/count`);
        setPrecommandesCount(response.data.count || 0);
      } catch (error) {
        console.error("Erreur vérification précommandes:", error);
      }
    };
    
    checkStock();
    checkPrecommandes();
    const interval = setInterval(checkStock, 5 * 60 * 1000);
    const pcInterval = setInterval(checkPrecommandes, 60 * 1000);
    
    return () => {
      clearInterval(interval);
      clearInterval(pcInterval);
    };
  }, [user]);

  const navItemsAll = [
    { path: "/", label: "Dashboard", icon: "📊", prefetch: "dashboard" }, // Toujours visible comme Paramètres
    { path: "/weekly-timeline", label: "Planning", icon: "📆", prefetch: "planning", moduleKey: "planning" },
    { path: "/gestion-equipes", label: "Sous-traitance", icon: "🤝", moduleKey: "gestion-equipes" },
    { path: "/photos", label: "Photos", icon: "📸", moduleKey: "photos" },
    { path: "/facturation", label: "Facturation", icon: "💰", prefetch: "facturation", moduleKey: "facturation" },
    { path: "/bilan", label: "Bilan", icon: "📊", prefetch: "bilan", moduleKey: "bilan" },
    { path: "/parures-sales", label: "Parures Sales", icon: "🛏️", moduleKey: "parures-sales" },
    { path: "/employees", label: "Employées", icon: "👥", moduleKey: "employees" },
    { path: "/clients", label: "Clients", icon: "👤", moduleKey: "clients" },
    { path: "/housings", label: "Logements et Lieux PRO", icon: "🏘️", moduleKey: "housings" },
    { path: "/ical-sync", label: "Synchro iCal", icon: "🔄", moduleKey: "ical-sync" },
    { path: "/stock-clients", label: "Stock Clients", icon: "🧴", moduleKey: "stock-clients" },
    { path: "/citations", label: "Citations", icon: "💬", moduleKey: "citations" },
    { path: "/integrations", label: "Intégrations", icon: "🔗", moduleKey: "integrations" },
  ];

  // Filtrage par permissions (si pas de moduleKey → toujours visible)
  const navItems = navItemsAll.filter(it => !it.moduleKey || canAccess(it.moduleKey));

  const adminNavItems = [
    { path: "/users", label: "👥 Utilisateurs", icon: "👥" },
  ];

  const superAdminNavItems = [
    { path: "/super-admin", label: "🔐 Super Admin", icon: "🔐" },
  ];

  const settingsNavItems = [
    { path: "/settings", label: "Parametres", icon: "⚙️" },
  ];

  return (
    <>
      {/* Navigation Mobile */}
      <div className="md:hidden">
        {/* Bannière mode visualisation tenant - Mobile */}
        {isViewingTenant && (
          <div className="bg-indigo-600 text-white px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-medium">👁️ {viewingTenantName}</span>
            <button
              onClick={returnToSuperAdmin}
              className="px-3 py-1 text-xs font-bold bg-white text-indigo-600 rounded hover:bg-indigo-50 transition-colors"
            >
              ← Super Admin
            </button>
          </div>
        )}
        <MobileNav 
          navItems={[
            ...navItems,
            ...(isAdmin() ? [{ path: "/users", label: "Utilisateurs", icon: "👥" }] : []),
            ...(isSuperAdmin() ? [{ path: "/super-admin", label: "Super Admin", icon: "🔐" }] : []),
            { path: "/settings", label: "Parametres", icon: "⚙️" },
          ]} 
          onLogout={logout} 
          userName={user?.full_name || user?.email}
        />
      </div>
      
      {/* Navigation Desktop - STICKY */}
      <nav className="bg-white shadow-sm border-b border-gray-200 hidden md:block sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header avec logo et titre - centré */}
          <div className="flex items-center justify-between py-5 border-b border-gray-100">
            <div className="flex items-center space-x-4 flex-1">
              {logoUrl ? (
                <img 
                  src={logoUrl}
                  alt={`${appName} Logo`}
                  className="h-14 w-14 object-contain rounded-full shadow-sm"
                />
              ) : (
                <div className="h-14 w-14 rounded-full bg-blue-600 flex items-center justify-center shadow-sm">
                  <span className="text-white text-2xl font-bold">{appName.charAt(0)}</span>
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-blue-600">
                  {appName}
                </h1>
                <p className="text-xs text-gray-500 mt-1 font-medium">{appSubtitle}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Bannière mode visualisation tenant */}
              {isViewingTenant && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg">
                  <span className="text-xs font-medium">👁️ Visualisation: {viewingTenantName}</span>
                  <button
                    onClick={returnToSuperAdmin}
                    className="px-2 py-1 text-xs font-bold bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                  >
                    ← Retour Super Admin
                  </button>
                </div>
              )}
              
              <span className="text-sm text-gray-700 font-medium">
                Admin {appName}
              </span>
              
              <div className="h-8 w-px bg-gray-200"></div>
              
              {settingsNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    location.pathname === item.path
                      ? "bg-blue-100 text-blue-700"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  {item.icon} {item.label}
                </Link>
              ))}
              
              {/* Lien Super Admin */}
              {isSuperAdmin() && (
                <Link
                  to="/super-admin"
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    location.pathname === "/super-admin"
                      ? "bg-purple-100 text-purple-700"
                      : "text-purple-600 hover:text-purple-900 hover:bg-purple-50"
                  }`}
                >
                  🔐 Super Admin
                </Link>
              )}
              
              <button
                onClick={logout}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors shadow-sm"
              >
                🚪 Déconnexion
              </button>
            </div>
          </div>
          
          {/* Navigation principale - une seule icône par onglet */}
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-0.5 py-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onMouseEnter={() => item.prefetch && prefetchOnHover(item.prefetch)}
                className={`inline-flex flex-col items-center justify-center w-[88px] h-20 px-1.5 py-2 text-xs font-semibold rounded-lg transition-all relative ${
                  location.pathname === item.path
                    ? "bg-blue-500 text-white shadow-lg"
                    : "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                }`}
                data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
              >
                <span className="text-2xl mb-1">{item.icon}</span>
                <span className="text-center leading-tight text-[11px] w-full truncate px-0.5">{item.label}</span>
                {/* Indicateur stock faible pour Stock Clients */}
                {item.path === "/stock-clients" && lowStock && (
                  <span 
                    className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center"
                    title="Stock faible détecté"
                  >
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-4 w-4 bg-red-600 text-white items-center justify-center text-xs font-bold">
                      !
                    </span>
                  </span>
                )}
                {/* Compteur de précommandes en attente */}
                {item.path === "/stock-clients" && precommandesCount > 0 && (
                  <span 
                    className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-yellow-500 rounded-full animate-pulse"
                    title={`${precommandesCount} précommande(s) en attente`}
                  >
                    🛒 {precommandesCount}
                  </span>
                )}
              </Link>
            ))}
            
            {/* Menu admin */}
            {isAdmin() && (
              <>
                <div className="h-8 w-px bg-gray-300 mx-2"></div>
                {adminNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === item.path
                        ? "bg-purple-100 text-purple-700"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                    }`}
                    data-testid={`nav-${item.path.replace("/", "") || "admin"}`}
                  >
                    <span className="mr-2">{item.icon}</span>
                    {item.label}
                  </Link>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
    </>
  );
};

// Home Component - Dashboard principal
const Home = () => {
  const [apiStatus, setApiStatus] = useState("Checking...");
  const { user, isSuperAdmin } = useAuth();

  useEffect(() => {
    const checkApiHealth = async () => {
      try {
        const response = await axios.get(`${API}/health`);
        setApiStatus(`✅ ${response.data.service} - ${response.data.status}`);
      } catch (error) {
        setApiStatus("❌ API non disponible");
        console.error("API Health check failed:", error);
      }
    };

    checkApiHealth();
  }, []);

  // Super Admin : ne pas charger le Dashboard (endpoints tenant-scoped),
  // afficher un message pour rediriger vers la sélection d'un client.
  // EXCEPTION : quand le Super Admin visualise un tenant précis (viewingTenantId set),
  // on affiche le vrai Dashboard comme s'il était admin de ce tenant.
  const viewingTenantId = typeof window !== "undefined" ? localStorage.getItem("viewingTenantId") : null;
  if (isSuperAdmin && isSuperAdmin() && !viewingTenantId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto py-16 px-4 text-center">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="text-5xl mb-4">👋</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              Bienvenue Super Admin
            </h1>
            <p className="text-gray-600 mb-6 text-base leading-relaxed" data-testid="super-admin-home-message">
              Veuillez sélectionner un client dans le tableau de bord Super Admin
              pour visualiser ses missions et employés.
            </p>
            <a
              href="/super-admin"
              data-testid="super-admin-go-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
            >
              <span>🔐</span>
              <span>Ouvrir le tableau de bord Super Admin</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Tableau de Bord
          </h1>
        </div>

        <Dashboard />
      </div>
    </div>
  );
};

// Layout avec Navigation
const Layout = ({ children, appConfig }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation appConfig={appConfig} />
      <main>{children}</main>
    </div>
  );
};

// Composant de protection des routes
const ProtectedRoute = ({ children, moduleKey }) => {
  const { user, loading, canAccess } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="loading-spinner"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Blocage module : si moduleKey fourni et user restreint, redirect vers Dashboard
  if (moduleKey && !canAccess(moduleKey)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Principal
function App() {
  return (
    <AuthProvider>
      <AppWithDemo />
    </AuthProvider>
  );
}

// Wrapper pour la démo et la configuration initiale
const AppWithDemo = () => {
  const demoConfig = useDemoConfig();
  const appConfig = useAppConfig();
  const { loading, needsSetup, recheckStatus } = useSetupStatus();
  
  // Si en cours de chargement du statut, afficher un loader
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }
  
  // Si l'app n'est pas encore configurée, afficher la page de setup
  if (needsSetup) {
    return (
      <FirstTimeSetup onSetupComplete={() => {
        recheckStatus();
        window.location.reload();
      }} />
    );
  }
  
  return (
    <>
      <AntiCloneProtection />
      <DemoBanner config={demoConfig} />
      <AppRoutes demoConfig={demoConfig} appConfig={appConfig} />
    </>
  );
};

// Routes de l'application
const AppRoutes = ({ demoConfig, appConfig }) => {
  const { user } = useAuth();

  return (
    <div className="App">
      <Toaster position="top-center" duration={2000} richColors />
      <BrowserRouter>
        <Routes>
          {/* Route publique de login */}
          <Route
            path="/login"
            element={
              user ? (
                user.is_super_admin || user.role === 'super_admin'
                  ? <Navigate to="/super-admin" replace />
                  : <Navigate to="/" replace />
              ) : <Login />
            }
          />
          
          {/* Route publique pour réinitialiser le mot de passe */}
          <Route
            path="/reset-password"
            element={<ResetPassword />}
          />

          {/* Route publique pour les employés (lien personnalisé) */}
          <Route
            path="/employee-day/:token"
            element={<EmployeeDay />}
          />
          
          {/* Route publique pour les liens courts */}
          <Route
            path="/e/:shortcode"
            element={<EmployeeDay useShortCode={true} />}
          />
          
          {/* Route publique pour le récapitulatif mensuel employé (lecture seule)
              → réutilise EmployeeDay avec le mode `useEmployeeId` pour garantir
              une UI 100 % identique aux liens /e/ et /employee-day/. */}
          <Route
            path="/planning-employe/:employeeId"
            element={<EmployeeDay useEmployeeId={true} />}
          />

          {/* Routes protégées */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout appConfig={appConfig}>
                    <Home />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/planning"
              element={
                <ProtectedRoute>
                  <Layout appConfig={appConfig}>
                    <PlanningCalendar />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/weekly-timeline"
              element={
                <ProtectedRoute moduleKey="planning">
                  <Layout appConfig={appConfig}>
                    <WeeklyTimeline />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/photos"
              element={
                <ProtectedRoute moduleKey="photos">
                  <Layout appConfig={appConfig}>
                    <PhotosManagerSimple />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sync-admin"
              element={
                <ProtectedRoute>
                  <Layout appConfig={appConfig}>
                    <SyncAdmin />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/grouped-planning"
              element={
                <ProtectedRoute>
                  <Layout appConfig={appConfig}>
                    <GroupedPlanning />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees"
              element={
                <ProtectedRoute moduleKey="employees">
                  <Layout appConfig={appConfig}>
                    <EmployeesManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <ProtectedRoute moduleKey="clients">
                  <Layout appConfig={appConfig}>
                    <ClientsManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/housings"
              element={
                <ProtectedRoute moduleKey="housings">
                  <Layout appConfig={appConfig}>
                    <HousingsManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ical-sync"
              element={
                <ProtectedRoute moduleKey="ical-sync">
                  <Layout appConfig={appConfig}>
                    <ICalSync />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stock-clients"
              element={
                <ProtectedRoute moduleKey="stock-clients">
                  <Layout appConfig={appConfig}>
                    <StockClients />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/facturation"
              element={
                <ProtectedRoute moduleKey="facturation">
                  <Layout appConfig={appConfig}>
                    <FacturationRecap />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bilan"
              element={
                <ProtectedRoute moduleKey="bilan">
                  <Layout appConfig={appConfig}>
                    <BilanRecap />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/parures-sales"
              element={
                <ProtectedRoute moduleKey="parures-sales">
                  <Layout appConfig={appConfig}>
                    <ParuresSalesRecap />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/citations"
              element={
                <ProtectedRoute moduleKey="citations">
                  <Layout appConfig={appConfig}>
                    <CitationsManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gestion-equipes"
              element={
                <ProtectedRoute moduleKey="gestion-equipes">
                  <Layout appConfig={appConfig}>
                    <GestionEquipes />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations"
              element={
                <ProtectedRoute moduleKey="integrations">
                  <Layout appConfig={appConfig}>
                    <IntegrationsManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <Layout appConfig={appConfig}>
                    <UsersManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout appConfig={appConfig}>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin"
              element={
                <ProtectedRoute>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parametrage"
              element={<Navigate to="/settings" replace />}
            />
            <Route
              path="/auth/google/callback"
              element={<GoogleCalendarCallback />}
            />
          </Routes>
        </BrowserRouter>
      </div>
  );
};

export default App;